import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { BsPeople } from 'react-icons/bs';

import { StyledInput } from '../../../components/StyledInput';
import { AdminTitle } from '../../../components/AdminTitle';
import { StyledDateInput } from '../../../components/StyledDateInput';
import { Actions } from '../../../components/Actions';
import StyledSelectCustomRaw from '../../../components/StyledSelectCustomRaw';
import { Loading } from '../../../components/Loading';

import { ufsNamed } from '../../../utils/ufsNamed';
import { categories } from '../../../utils/categories';
import formatMobilePhoneNumber from '../../../utils/formatMobilePhoneNumber';
import { formatDocument } from '../../../utils/formatDocument';
import { TYPE_DOCUMENT } from '../../../utils/enums';
import { emailValidation } from '../../../utils/EmailValidation';
import formatCep from '../../../utils/formatCep';

import { Container, Card, FormWrapper, FormContainer } from './style';
import theme from '../../../styles/themes/default';
import { validateCpf } from '../../../utils/validateCpf';
import { getDriverById, postDriver, putDriver } from '../../../hooks/useDriver';
import moment from 'moment';
import { handleSearchCep } from '../../../utils/handleSearchCep';
import { useQueryClient } from 'react-query';

const maxBirthDate = new Date();
maxBirthDate.setFullYear(maxBirthDate.getFullYear() - 18);

const DriversForm: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const queryClient = useQueryClient();

  const [name, setName] = useState<string>('');
  const [cpf, setCpf] = useState<string>('');
  const [birthDate, setBirthDate] = useState<Date>();
  const [email, setEmail] = useState<string>('');
  const [telephone, setTelephone] = useState<string>('');
  const [cep, setCep] = useState<string>('');
  const [state, setState] = useState<string | undefined>('');
  const [neighborhood, setNeighborhood] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [number, setNumber] = useState<string>('');
  const [complement, setComplement] = useState<string>('');
  const [numberCnh, setNumberCnh] = useState<string | null>();
  const [validateCnh, setValidateCnh] = useState<Date>();
  const [emission, setEmission] = useState<Date>();
  const [fatherName, setFatherName] = useState<string>('');
  const [motherName, setMotherName] = useState<string>('');
  const [rg, setRg] = useState<string>('');
  const [category, setCategory] = useState('');

  const requiredField = (value: any) => {
    return value ? '' : 'Campo obrigatório';
  };

  const validBirthDate = (value: Date | undefined) => {
    return value && value <= maxBirthDate
      ? ''
      : 'Condutor deve ser maior de 18 anos';
  };

  const emailError = useMemo(() => {
    const result = emailValidation({
      email,
      emptyEmail: 'Campo obrigatório',
      incorrectEmail: 'Email inválido',
    });
    return result.message;
  }, [email]);

  const cpfError = useMemo(() => {
    const result = validateCpf({
      cpf,
      emptyCpf: 'Campo obrigatório',
      incorrectCpf: 'CPF inválido',
    });
    return result.message;
  }, [cpf]);

  const handleFormValidate = useCallback(() => {
    const validations = [
      requiredField(name),
      requiredField(cpf),
      requiredField(birthDate),
      validBirthDate(birthDate),
      cpfError,
      requiredField(email),
      emailError,
      requiredField(telephone),
    ];
    return !validations.some((v) => v);
  }, [name, cpf, cpfError, birthDate, email, emailError, telephone]);

  const handleCep = async (cep: string) => {
    if (cep.length === 8) {
      try {
        setLoading(true);
        const result = await handleSearchCep(cep);

        if (result.erro) {
          Swal.fire({
            icon: 'error',
            title: 'Erro!',
            html: 'Não foi possivel encontrar o cep informado. Verifique e tente novamente!',
            confirmButtonColor: theme.colors.orange.primary,
          });
        }

        const state =
          ufsNamed.find(({ id }) => result.uf && id === result.uf)?.id || '';

        setState(state);
        setNeighborhood(result.bairro);
        setStreet(result.logradouro);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }
    setCep(formatCep(cep));
  };

  const handleLoadDriver = async () => {
    try {
      setLoading(true);
      const driver = await getDriverById(Number(id));
      setName(driver.name);
      setCpf(driver.cpf);

      if (driver.birth_date) {
        setBirthDate(moment(driver.birth_date).toDate());
      }
      setEmail(driver.email);
      setTelephone(driver.telephone);
      setCep(driver.cep);
      setState(driver.state);
      setNeighborhood(driver.neighborhood);
      setStreet(driver.street);
      setNumber(driver.number);
      setComplement(driver.complement ? driver.complement : '');
      setNumberCnh(driver.number_cnh);

      if (driver.validate_cnh) {
        setValidateCnh(moment(driver.validate_cnh).toDate());
      }
      setFatherName(driver.father_name);
      setMotherName(driver.mother_name);
      setRg(driver.rg);
      if (driver.category) {
        setCategory(JSON.parse(driver.category)[0]);
      }

      if ((driver.emission || '').trim().length > 0) {
        setEmission(moment(driver.emission, 'DD/MM/yyyy').toDate());
      }
    } catch (error: any) {
      console.log(error);
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        html: error?.response?.data?.message ?? `Condutor não encontrado.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setValidating(true);
    if (!handleFormValidate()) {
      Swal.fire({
        icon: 'error',
        title: 'Campos inválidos!',
        html: `Alguns campos ainda estão inválidos. Corrija-os e tente novamente.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
      return;
    }

    try {
      setLoading(true);
      const emissionString = emission
        ? moment(emission).format('DD/MM/yyyy')
        : '';

      const payload = {
        name,
        cpf,
        birth_date: birthDate,
        email,
        telephone,
        cep,
        state,
        neighborhood,
        street,
        number,
        complement,
        number_cnh: numberCnh ? numberCnh : null,
        validate_cnh: validateCnh,
        emission: emissionString,
        father_name: fatherName,
        mother_name: motherName,
        rg,
        category: category ? category : null,
      };

      if (id) {
        await putDriver({ id: Number(id), ...payload });
      } else {
        await postDriver(payload);
      }
      await queryClient.invalidateQueries('VEHICLES');
      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        html: `Condutor ${id ? 'editado' : 'cadastrado'} com sucesso.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
      navigate('/drivers', {
        state: {
          shouldRefetch: true,
        },
      });
    } catch (error: any) {
      if (
        error?.response?.data?.message?.toLowerCase().includes('driver already')
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          html: 'CPF já cadastrado para outro condutor.',
          confirmButtonColor: theme.colors.orange.primary,
        });
      }

      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        html:
          error.response.data.message ??
          `Não foi possível salvar este registro.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      handleLoadDriver();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Container>
      {loading && <Loading />}
      <AdminTitle
        title="Novo cadastro"
        icon={<BsPeople size={24} color="#4D4C4D" />}
      />
      <FormContainer onSubmit={handleSubmit}>
        <Card>
          <h2>Informações Gerais</h2>
          <FormWrapper>
            <label htmlFor="">
              <StyledInput
                placeholder="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={validating && !!requiredField(name)}
                errorMessage={requiredField(name)}
                maxLength={255}
              />
            </label>
            <label style={{ maxWidth: '32.7%' }} htmlFor="">
              <StyledInput
                placeholder="CPF"
                value={cpf}
                onChange={(e) =>
                  setCpf(formatDocument(e.target.value, TYPE_DOCUMENT.CPF))
                }
                maxLength={14}
                error={validating && !!cpfError}
                errorMessage={cpfError}
              />
            </label>
          </FormWrapper>
          <FormWrapper error={validating}>
            <label htmlFor="">
              <StyledDateInput
                value={birthDate}
                onChange={(e) => setBirthDate(e)}
                placeholder="Data de nascimento"
                maxDate={new Date()}
                error={
                  validating &&
                  (!!requiredField(birthDate) || !!validBirthDate(birthDate))
                }
                errorMessage={
                  requiredField(birthDate) || validBirthDate(birthDate)
                }
              />
            </label>

            <label htmlFor="">
              <StyledInput
                placeholder="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={validating && !!emailError}
                errorMessage={emailError}
              />
            </label>
            <label htmlFor="">
              <StyledInput
                placeholder="Telefone"
                value={telephone}
                onChange={(e) =>
                  setTelephone(formatMobilePhoneNumber(e.target.value))
                }
                maxLength={15}
                error={validating && !!requiredField(telephone)}
                errorMessage={requiredField(telephone)}
              />
            </label>
          </FormWrapper>
        </Card>
        <Card>
          <h2>Endereço</h2>
          <FormWrapper>
            <label htmlFor="">
              <StyledInput
                placeholder="CEP"
                value={cep}
                maxLength={8}
                onChange={(e) => handleCep(e.target.value)}
              />
            </label>
            <label htmlFor="">
              <StyledSelectCustomRaw
                optionsList={ufsNamed}
                selectValue={
                  ufsNamed.find(({ id }) => state && id === state)?.value || ''
                }
                setSelectValue={setState}
                placeHolder={'Estado'}
                placeHolderOnList={'Selecione...'}
                width={''}
                flexItem={1}
                height={'48px'}
                fontSize={'16px'}
                fontWeight={600}
                fontColor={theme.colors.gray[900]}
                border={'1px solid'}
                borderColor={theme.colors.gray[400]}
                borderOnList={'1px solid'}
                borderColorOnList={theme.colors.gray[400]}
                widthOptionsList={'100%'}
                heightOptionsList={'236px'}
                hoverBackgroundColorOnList={theme.colors.blue.primary}
                hoverColorOnList={theme.colors.white}
                optionsFontSize={'16px'}
                optionsFontColor={theme.colors.gray[500]}
                backgroundColorOnList={theme.colors.white}
                arrowIconSize={24}
                arrowIconColor={theme.colors.gray[500]}
                padding={'0 16px'}
                paddingOnList={'4px 16px'}
                borderRadius={'8px'}
                borderRadiusOnList={'8px'}
                focusBorderColor={theme.colors.orange.primary}
              />
            </label>
            <label htmlFor="">
              <StyledInput
                placeholder="Bairro"
                value={neighborhood}
                onChange={(e) => setNeighborhood(e.target.value)}
              />
            </label>
          </FormWrapper>
          <FormWrapper>
            <label htmlFor="">
              <StyledInput
                placeholder="Logadouro"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
              />
            </label>
            <label htmlFor="">
              <StyledInput
                placeholder="Número"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
              />
            </label>
            <label htmlFor="">
              <StyledInput
                placeholder="Complemento (Opcional)"
                value={complement}
                onChange={(e) => setComplement(e.target.value)}
              />
            </label>
          </FormWrapper>
        </Card>
        <Card>
          <h2>CNH</h2>
          <FormWrapper>
            <label htmlFor="">
              <StyledInput
                placeholder="Nº registro CNH"
                value={numberCnh as string}
                onChange={(e) => setNumberCnh(e.target.value)}
                maxLength={11}
              />
            </label>
            <label htmlFor="">
              <StyledDateInput
                value={validateCnh}
                onChange={(e) => setValidateCnh(e)}
                placeholder="Validade"
              />
            </label>
            <label htmlFor="">
              <StyledDateInput
                onChange={(e) => setEmission(e)}
                placeholder="Emissão"
                value={emission}
              />
            </label>
          </FormWrapper>
          <FormWrapper error={validating}>
            <label htmlFor="">
              <StyledInput
                placeholder="Nome do pai"
                value={fatherName}
                onChange={(e) => setFatherName(e.target.value)}
              />
            </label>
            <label htmlFor="">
              <StyledInput
                placeholder="Nome da mãe"
                value={motherName}
                onChange={(e) => setMotherName(e.target.value)}
              />
            </label>
            <label htmlFor="">
              <StyledInput
                placeholder="RG"
                value={rg}
                maxLength={9}
                onChange={(e) =>
                  setRg(formatDocument(e.target.value, TYPE_DOCUMENT.RG))
                }
              />
            </label>
          </FormWrapper>
          <FormWrapper error={validating}>
            <label htmlFor="">
              <StyledSelectCustomRaw
                optionsList={categories}
                selectValue={
                  categories.find(({ id }) => category && id === category)
                    ?.value || ''
                }
                setSelectValue={setCategory}
                placeHolder={'Categoria'}
                placeHolderOnList={'Selecione...'}
                width={''}
                flexItem={1}
                height={'48px'}
                fontSize={'16px'}
                fontWeight={600}
                fontColor={theme.colors.gray[900]}
                border={'1px solid'}
                borderColor={theme.colors.gray[400]}
                borderOnList={'1px solid'}
                borderColorOnList={theme.colors.gray[400]}
                widthOptionsList={'100%'}
                heightOptionsList={'236px'}
                hoverBackgroundColorOnList={theme.colors.blue.primary}
                hoverColorOnList={theme.colors.white}
                optionsFontSize={'16px'}
                optionsFontColor={theme.colors.gray[500]}
                backgroundColorOnList={theme.colors.white}
                arrowIconSize={24}
                arrowIconColor={theme.colors.gray[500]}
                padding={'0 16px'}
                paddingOnList={'4px 16px'}
                borderRadius={'8px'}
                borderRadiusOnList={'8px'}
                focusBorderColor={theme.colors.orange.primary}
              />
            </label>
            <label htmlFor=""></label>
            <label htmlFor=""></label>
          </FormWrapper>
        </Card>

        <Actions onCancel={() => navigate('/drivers')} />
      </FormContainer>
    </Container>
  );
};

export default DriversForm;
